import React from 'react';
import PropTypes from 'prop-types';
import { ListGroup } from 'reactstrap';
import { useIntl } from 'react-intl';

import AdminEditButtons from '../EditButtons';
import AdminRoleEditCapabilitiesItem from './CapabilitiesItem';

import { downloadReasonsEnabled } from '../../../helpers/Download';
import { getSetting } from '../../../helpers/Settings';

import ErrorBoundary from '../../ErrorBoundary';
import Loading from '../../Loading';

const AdminRoleEditCapabilitiesEdit = ({
  role,
  capabilities,
  capabilitiesLoading,
  handleCapabilitySwitch,
  handleChangeCapabilityAttributes,
  ...props
}) => {
  const intl = useIntl();

  const capabilitySelected = (cap) => {
    const { capabilities: roleCap } = role;
    return roleCap.includes(cap);
  };

  const shouldBeDisabled = (cap, ignoreForChecked = false) => {
    const { capabilities: roleCap } = role;

    const ignoreIndexesForChecked = ['edit_people'];

    if (ignoreForChecked && ignoreIndexesForChecked.includes(cap)) {
      return false;
    }

    if (cap === 'use_image_editor') {
      return !(roleCap.includes('download_files_and_collections') || roleCap.includes('request_download_files_and_collections'));
    }

    if (cap === 'delete_files_collections_permanent') {
      return !(roleCap.includes('delete_files_and_collections'));
    }

    if (cap === 'public_file_urls') {
      return !getSetting('public_file_urls');
    }

    if (cap === 'edit_people') {
      return roleCap.includes('edit_file');
    }

    if (cap === 'create_public_file') {
      return !(roleCap.includes('upload_file'));
    }

    return false;
  };

  if (capabilitiesLoading) {
    return (
      <Loading />
    );
  }

  const { general = {} } = capabilities;
  const { files = {} } = capabilities;
  const { collections = {} } = capabilities;

  const shouldIncludeDownloadReasons = downloadReasonsEnabled();

  const filteredFilesCaps = Object.fromEntries(
    Object.entries(files).filter(([key]) => {
      if (key === 'reason_download_files_and_collections') {
        return shouldIncludeDownloadReasons;
      }

      if (key === 'image_editor_save_versions') {
        return capabilitySelected('edit_file') && capabilitySelected('use_image_editor');
      }

      return true;
    }),
  );

  const filteredCollectionsCaps = Object.fromEntries(
    Object.entries(collections).filter(([key]) => key !== 'reason_download_files_and_collections' || shouldIncludeDownloadReasons),
  );

  return (
    <ErrorBoundary>
      <div className="tab-content-wrapper">
        <p>
          {intl.formatMessage({ defaultMessage: 'Set the Custom Role capabilities.' })}
        </p>
        <div className="group-permissions permissions-lists">
          {Object.keys(general).length > 0 && (
            <ErrorBoundary>
              <h5 className="permissions-title">
                {intl.formatMessage({ defaultMessage: 'General' })}
              </h5>
              <ListGroup flush className="switch-list group-permissions-list general-permissions-list">
                {Object.keys(general).map(c => (
                  <AdminRoleEditCapabilitiesItem
                    key={c}
                    capability={c}
                    capabilityName={general[c]}
                    checked={capabilitySelected(c) && !shouldBeDisabled(c, true)}
                    capabilityAttributes={role.capability_attributes[c] || []}
                    handleCapabilitySwitch={handleCapabilitySwitch}
                    handleChangeCapabilityAttributes={handleChangeCapabilityAttributes}
                    disabled={shouldBeDisabled(c)}
                  />
                ))}
              </ListGroup>
            </ErrorBoundary>
          )}
          {Object.keys(filteredFilesCaps).length > 0 && (
            <ErrorBoundary>
              <h5 className="permissions-title">
                {intl.formatMessage({ defaultMessage: 'Files' })}
              </h5>
              <ListGroup flush className="switch-list group-permissions-list files-permissions-list">
                {Object.keys(filteredFilesCaps).map(c => (
                  <AdminRoleEditCapabilitiesItem
                    key={c}
                    capability={c}
                    capabilityName={files[c]}
                    checked={capabilitySelected(c) && !shouldBeDisabled(c, true)}
                    capabilityAttributes={role.capability_attributes[c] || []}
                    handleCapabilitySwitch={handleCapabilitySwitch}
                    handleChangeCapabilityAttributes={handleChangeCapabilityAttributes}
                    disabled={shouldBeDisabled(c)}
                  />
                ))}
              </ListGroup>
            </ErrorBoundary>
          )}
          {Object.keys(filteredCollectionsCaps).length > 0 && (
            <ErrorBoundary>
              <h5 className="permissions-title">
                {intl.formatMessage({ defaultMessage: 'Collections' })}
              </h5>
              <ListGroup flush className="switch-list group-permissions-list collections-permissions-list">
                {Object.keys(filteredCollectionsCaps).map(c => (
                  <AdminRoleEditCapabilitiesItem
                    key={c}
                    capability={c}
                    capabilityName={collections[c]}
                    checked={capabilitySelected(c) && !shouldBeDisabled(c, true)}
                    capabilityAttributes={role.capability_attributes[c] || []}
                    handleCapabilitySwitch={handleCapabilitySwitch}
                    handleChangeCapabilityAttributes={handleChangeCapabilityAttributes}
                    disabled={shouldBeDisabled(c)}
                  />
                ))}
              </ListGroup>
            </ErrorBoundary>
          )}
        </div>
      </div>
      <AdminEditButtons
        {...props}
        deleteText={intl.formatMessage({ defaultMessage: 'Delete Role' })}
      />
    </ErrorBoundary>
  );
};

AdminRoleEditCapabilitiesEdit.propTypes = {
  role: PropTypes.object.isRequired,
  capabilities: PropTypes.object.isRequired,
  capabilitiesLoading: PropTypes.bool.isRequired,
  handleCapabilitySwitch: PropTypes.func.isRequired,
  handleChangeCapabilityAttributes: PropTypes.func.isRequired,
};

export default AdminRoleEditCapabilitiesEdit;
